<template>
  <!-- Contenido de INFO del parque (una columna, SIN imagen): barra de progreso del
       proyecto + tabla de capas + tabla de power blocks. Se monta dentro del body del
       panel lateral. Componente→servicio→api. -->
  <div class="epc-info-content">
    <div v-if="!loaded" class="epc-info-loading">
      <b-loading :is-full-page="false" :active="true"></b-loading>
    </div>

    <!-- Detalle de un power block enfocado (doble click en el visor): reemplaza la info
         general del parque por el detalle del bloque. -->
    <ViewerBlockInfoContent v-else-if="focusedBlock"
      :block="focusedBlock"
      :company-alias="companyAlias"
      :active-layer="activeLayer" />

    <template v-else>
      <!-- Progreso del proyecto (gráfico de barra) -->
      <div class="epc-info-block">
        <div class="epc-info-progress-head">
          <span class="epc-info-progress-label">{{ $t('pv_dashboard_project_progress') }}</span>
          <span class="epc-info-progress-pct">{{ projectProgress }}%</span>
        </div>
        <column-chart :data="progressChartData" :min="0" :max="100" suffix="%"
          :colors="['#3a6bc8']" :library="chartLibrary" height="130px"></column-chart>
      </div>

      <!-- Tabla de componentes (capas) -->
      <div class="epc-info-block">
        <h5 class="epc-info-title">{{ $t('components') }}</h5>
        <SummaryTable
          :layers="defaultLayers"
          :company-alias="companyAlias"
          :type-label="$t('component')"
          :get-total="getTotalElements"
          :get-executed="getExecutedElements"
          :get-incidents="getTotalIncidentElements"
          :get-progress="getProgressPictograms"
          :use-executed-ratio="companyAlias === 'ignis'"
        />
      </div>

      <!-- Tabla de power blocks -->
      <div v-if="pictograms.length" class="epc-info-block">
        <h5 class="epc-info-title">{{ $t('pictograms') }}</h5>
        <table class="epc-pb-table w-full border border-blue-lighter border-collapse">
          <tbody>
            <tr v-for="item in pictograms" :key="item.id"
              :ref="'pbrow_' + item.id"
              class="epc-pb-row"
              :class="{ 'epc-pb-row--active': String(item.id) === String(hoveredBlockId) }">
              <td class="border border-blue-lighter text-gray-800">
                {{ item.activity_planning_code + ' - ' + item.activity_associated }}
              </td>
              <td class="border border-blue-lighter text-gray-800 text-right whitespace-nowrap">
                {{ item.activity_progress }} <span class="ml-1">%</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from "vue"
import VueChartkick from "vue-chartkick"
import Chart from "chart.js"
import SummaryTable from "../../../dashboard/SummaryTable.vue"
import ViewerBlockInfoContent from "./ViewerBlockInfoContent.vue"
import { fetchProjectPictograms } from "../../services/power-plant.service"

// Registro de chartkick (idempotente): para <column-chart> del progreso.
Vue.use(VueChartkick, { adapter: Chart })

export default {
  name: "ViewerInfoContent",

  components: { SummaryTable, ViewerBlockInfoContent },

  props: {
    projectId: { type: Number, required: true },
    hoveredBlockId: { type: [Number, String], default: null }, // bloque en hover → resalta fila
    focusedBlockId: { type: [Number, String], default: null }, // bloque enfocado → su detalle
    activeLayer: { type: String, default: null }, // vista/capa actual → se resalta en el detalle
  },

  data() {
    return {
      pictograms: [],
      isLoading: false,
      loaded: false,
    }
  },

  computed: {
    companyAlias() {
      const company = this.$store.getters['app/company']
      return (company && company.alias) || ''
    },
    defaultLayers() {
      return [{ code: 'pilings' }, { code: 'structures' }, { code: 'modules' }]
    },
    projectProgress() {
      if (!this.pictograms.length) return 0
      let total = 0
      this.pictograms.forEach(item => { total += parseFloat(item.activity_progress) || 0 })
      return this._truncate(total / this.pictograms.length, 2)
    },
    progressChartData() {
      return [[this.$t('progress'), Number(this.projectProgress)]]
    },
    // Pictograma del bloque enfocado (si lo hay y ya está cargado en la lista).
    focusedBlock() {
      if (this.focusedBlockId == null) return null
      return this.pictograms.find(p => String(p.id) === String(this.focusedBlockId)) || null
    },
    chartLibrary() {
      return { legend: { display: false } }
    },
  },

  watch: {
    // Al cambiar el bloque en hover, llevar su fila a la vista.
    hoveredBlockId(id) {
      if (id == null) return
      this.$nextTick(() => {
        const ref = this.$refs['pbrow_' + id]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
      })
    },
  },

  mounted() {
    this._Load()
  },

  methods: {
    async _Load() {
      this.isLoading = true
      try {
        // Sin screenshot (caro) ni power plant: el info no usa la imagen → consulta rápida.
        const { data } = await fetchProjectPictograms(this.projectId, { withScreenshot: false, withPowerPlant: false })
        if (!data || !data.success) {
          this.$notify.error('error_loading_documents')
          return
        }
        // Mismo filtro que el dashboard: con actividad y sin los de retrabajo (.R.).
        this.pictograms = (data.pictograms || []).filter(
          el => el.activity_planning_code && !el.activity_planning_code.includes('.R.')
        )
        this.loaded = true
      } catch (e) {
        this.$notify.error('error_loading_documents')
      } finally {
        this.isLoading = false
      }
    },

    // --- Derivación de la tabla de capas (misma lógica que dashboard/ProgressOverview) ---
    getTotalElements(lyr) {
      let total = 0
      this.pictograms.forEach(item => {
        if (item.type === 'mechanical_installation') total += parseFloat((item.metadata || {})['total_' + lyr.code]) || 0
      })
      return total
    },
    getExecutedElements(lyr) {
      let total = 0
      if (this.companyAlias === 'x-elio') {
        this.pictograms.forEach(item => {
          const tp = (item.metadata || {}).total_progress
          if (tp != null) total += parseFloat(tp[lyr.code] ?? 0) || 0
        })
      } else {
        this.pictograms.forEach(item => {
          const layer = (item.layers || []).find(l => l.code === lyr.code)
          total += parseFloat(layer && layer.metadata && layer.metadata.progress_layer ? (layer.metadata.progress_layer.elements_active ?? 0) : 0) || 0
        })
      }
      return total
    },
    getTotalIncidentElements(lyr) {
      let total = 0
      this.pictograms.forEach(item => {
        (item.layers || []).forEach(layer => {
          if (layer.code === lyr.code && layer.metadata && layer.metadata.incident_statuses != null) {
            for (const key in layer.metadata.incident_statuses) {
              if (Object.prototype.hasOwnProperty.call(layer.metadata.incident_statuses, key)) {
                total += parseFloat(layer.metadata.incident_statuses[key].advance ?? 0) || 0
              }
            }
          }
        })
      })
      return total
    },
    getProgressPictograms(lyr) {
      if (!this.pictograms.length) return 0
      let total = 0
      this.pictograms.forEach(item => {
        if (item.type === 'mechanical_installation') {
          const layer = (item.layers || []).find(l => l.code === lyr.code)
          const metadata = layer ? layer.metadata : null
          if (metadata && metadata.progress_layer != null) total += parseFloat(metadata.progress_layer.advance_normalize ?? 0) || 0
        }
      })
      return total / this.pictograms.length
    },

    _truncate(num, decimals) {
      const factor = Math.pow(10, decimals)
      return Math.floor(num * factor) / factor
    },
  },
}
</script>

<style scoped>
.epc-info-content {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.epc-info-loading {
  position: relative;
  min-height: 200px;
}

.epc-info-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.epc-info-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a93a3;
  margin: 0;
}

.epc-info-progress-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.epc-info-progress-label {
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.epc-info-progress-pct {
  font-size: 16px;
  font-weight: 800;
  color: #3a6bc8;
}

/* Tablas compactas (incluye SummaryTable vía deep). */
.epc-info-content ::v-deep table td,
.epc-info-content ::v-deep table th {
  padding: 4px 7px;
  font-size: 11px;
  line-height: 1.25;
}

/* Fila de power block: hover + resaltado cuando el bloque está en hover en el visor. */
.epc-pb-row {
  transition: background-color 0.1s ease;
}

.epc-pb-row:hover {
  background-color: #f4f6fb;
}

.epc-pb-row--active {
  background-color: #e8eef8;
  box-shadow: inset 3px 0 0 #3a6bc8; /* línea azul a la izquierda */
}
</style>
